<template>
  <div v-if="tooltipData" class="mgx_hover_card_singleton" :class="tooltipData.directionClass" :style="tooltipData.style">
    <div class="mgx_hover_card_header">
      <span class="mgx_hover_card_title">{{ t('magnet_trigger_trip') }}</span>
      <span class="mgx_hover_card_hint">mm</span>
    </div>
    <div class="mgx_hover_card_grid">
      <div class="mgx_hover_item orange">
        <span class="mgx_hover_item_label">{{ t('magnet_trigger_trip') }}</span>
        <span class="mgx_hover_item_value">{{ tooltipData.pressText }}</span>
      </div>
      <div class="mgx_hover_item blue">
        <span class="mgx_hover_item_label">{{ t('magnet_quick_reset_sensitivity') }}</span>
        <span class="mgx_hover_item_value">{{ tooltipData.quickResetText }}</span>
      </div>
      <div class="mgx_hover_item green">
        <span class="mgx_hover_item_label">{{ t('magnet_quick_trigger_sensitivity') }}</span>
        <span class="mgx_hover_item_value">{{ tooltipData.quickTriggerText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, StyleValue } from 'vue'
import { operate, OperateType } from '@/service/driver/operate'
import { magnetSet } from '@/service/driver/magnet_set'
import { KK } from '@/define/define'
import { IMCSwitchState, isFE87V2IMCNH } from '../mgx_set/IMC_switch.vue'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import i18n from '@/languages/i18n'
import { convertTravel } from '@/tool/utility'
import { MagnetKeyType, MagnetSeniorType } from '@libs/types/enum'
import { setData } from '@/service/driver/data'

const props = defineProps<{
  hoverElement: HTMLElement | null
  hoverKey: KK | null
  stageElement: HTMLElement | null
}>()

const { t } = i18n.global

const setMagnetTexts = {
  [MagnetKeyType.Macro]: 'Macro',
  [MagnetKeyType.DKS]: 'DKS',
  [MagnetKeyType.MT]: 'MT',
  [MagnetKeyType.TGL]: 'TGL',
  [MagnetKeyType.MPT]: 'MPT',
  [MagnetKeyType.END]: 'END',
  [MagnetKeyType.KME]: 'KME',
  [MagnetKeyType.RS]: 'RS',
}

const isDKS = (kk: KK): boolean => {
  return setData.getData().Layout.Magenet[kk] && setData.getData().Layout.Magenet[kk].KeyType == MagnetSeniorType.SeniorKeyTypeDKS
}

const formatTooltipTravel = (num: number | null | undefined): string => {
  if (num == null || num <= 0) {
    return '--'
  }
  return `${convertTravel(num)}mm`
}

const getHoverMagnetKeyType = (kk: KK): string | null => {
  if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
    return null
  }
  if (specialCheckCurrent(speciaBehiver.DevicesOfWitmod)) {
    return isDKS(kk) ? 'DKS' : null
  }

  const data = magnetSet.list[kk]
  if (!data) {
    return null
  }

  const mappedText = setMagnetTexts[data.KeyType] ?? null

  if (specialCheckCurrent(speciaBehiver.SupportKME) || specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
    return data.KeyType != MagnetKeyType.KME ? mappedText : null
  }

  return mappedText
}

const tooltipData = computed(() => {
  if (operate.getActionType().value != OperateType.mgx) {
    return null
  }
  if (!props.hoverElement || !props.stageElement || props.hoverKey === null) {
    return null
  }
  if (IMCSwitchState.value || isFE87V2IMCNH()) {
    return null
  }
  if (getHoverMagnetKeyType(props.hoverKey) !== null) {
    return null
  }

  const data = magnetSet.list[props.hoverKey]
  if (!data) {
    return null
  }

  const stageRect = props.stageElement.getBoundingClientRect()
  const keyRect = props.hoverElement.getBoundingClientRect()
  const stageWidth = props.stageElement.offsetWidth
  const stageHeight = props.stageElement.offsetHeight
  const scaleX = stageWidth > 0 ? stageRect.width / stageWidth : 1
  const scaleY = stageHeight > 0 ? stageRect.height / stageHeight : 1
  const showBelow = keyRect.top - stageRect.top < 96
  const style: StyleValue = {
    left: `${(keyRect.left - stageRect.left + keyRect.width / 2) / scaleX}px`,
    top: showBelow ? `${(keyRect.bottom - stageRect.top + 10) / scaleY}px` : 'auto',
    bottom: showBelow ? 'auto' : `${(stageRect.bottom - keyRect.top + 10) / scaleY}px`,
  }

  return {
    style,
    directionClass: showBelow ? 'is_below' : 'is_above',
    pressText: formatTooltipTravel(data.PressNum > 0 ? data.PressNum : null),
    quickResetText: formatTooltipTravel(data.QuickTriggerReleaseNum > 0 ? data.QuickTriggerReleaseNum : null),
    quickTriggerText: formatTooltipTravel(data.QuickTriggerPressNum > 0 ? data.QuickTriggerPressNum : null),
  }
})
</script>

<style lang="less">
.mgx_hover_card_singleton {
  position: absolute;
  width: 148px;
  padding: 8px;
  transform: translateX(-50%);
  background: var(--bg-card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-soft);
  pointer-events: none;
  z-index: 2100;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 12px;
    height: 12px;
    background: var(--bg-card);
    transform: translateX(-50%) rotate(45deg);
  }

  &.is_above::after {
    bottom: -6px;
    border-right: 1px solid rgba(0, 0, 0, 0.06);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  &.is_below::after {
    top: -6px;
    border-left: 1px solid rgba(0, 0, 0, 0.06);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .mgx_hover_card_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .mgx_hover_card_title {
    color: var(--color-dark);
    font-size: 11px;
    font-weight: 700;
  }

  .mgx_hover_card_hint {
    color: var(--color-text-hint);
    font-size: 10px;
    font-weight: 600;
    opacity: 0.72;
  }

  .mgx_hover_card_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .mgx_hover_item {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 6px 7px;
    border-radius: var(--radius-s);
    background: rgba(255, 255, 255, 0.9);
    min-width: 0;
  }

  .mgx_hover_item_label {
    color: var(--color-text-hint);
    font-size: 9px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mgx_hover_item_value {
    color: var(--color-dark);
    font-size: 10px;
    font-weight: 700;
    line-height: 1.1;
  }

  .mgx_hover_item.orange {
    box-shadow: inset 0 0 0 1px rgba(var(--color-brand-2-rgb), 0.08);
  }

  .mgx_hover_item.orange .mgx_hover_item_value {
    color: var(--brand-color-pure);
  }

  .mgx_hover_item.blue {
    box-shadow: inset 0 0 0 1px rgba(43, 127, 255, 0.12);
  }

  .mgx_hover_item.blue .mgx_hover_item_value {
    color: #2b7fff;
  }

  .mgx_hover_item.green {
    box-shadow: inset 0 0 0 1px rgba(0, 188, 125, 0.12);
  }

  .mgx_hover_item.green .mgx_hover_item_value {
    color: #00bc7d;
  }
}
</style>
